import React from 'react';
import styles from './css/index.module.css';
import { Input, Tooltip, Select } from '@fluentui/react-components';
import { GetMergedCompanyProfile, PatchProjectData, PostProjectData } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, add } from 'date-fns';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Info12Filled } from '@fluentui/react-icons';
import { resolveWorkspaceProjectTypeSelectOptions } from '../../utils/projectTypes';
import {
    newProjectCreateFormValues,
    resolveWorkspaceInvestmentTypeOptions,
} from '../../utils/projectMetadataOptions';
import { applyCompanyEnrichment, buildEnrichmentPatchPayload } from '../../utils/companyEnrichment';

const AddProject = ({ setSelectedView, setCreatedProjectData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [formData, setFormData] = React.useState({
        project_name: '',
        investor_name: '',
        website: '',
        project_deadline: format(add(new Date(), { months: 1 }), 'yyyy-MM-dd'),
        company_name: '',
        project_type: '',
        ...newProjectCreateFormValues(),
    });
    const { projectDetailButtonLoading } = useSelector(state => state.projectData);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const activeTenant = React.useMemo(() => {
        const m = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return m?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const projectTypeOptions = React.useMemo(
        () => resolveWorkspaceProjectTypeSelectOptions(activeTenant, { includeValue: formData?.project_type }),
        [activeTenant, formData?.project_type],
    );

    const investmentTypeOptions = React.useMemo(
        () => resolveWorkspaceInvestmentTypeOptions(activeTenant, { includeValue: formData?.investment_type }),
        [activeTenant, formData?.investment_type],
    );

    const handleSubmit = async e => {
        e.preventDefault();
        try {
            const createResponse = await dispatch(
                PostProjectData({
                    body: {
                        ...formData,
                        customer_type: '',
                        customer_stage: '',
                        scope_areas: [],
                        engagement_objective: '',
                        project_deadline: format(new Date(formData?.project_deadline), 'dd-MM-yyyy'),
                    },
                    callback: () => {},
                    suppressSuccessToast: true,
                }),
            );
            if (createResponse?.type !== 'projectData/postProjectData/fulfilled') {
                return;
            }

            toast.success('Project Created', { position: 'top-right' });

            const newProjectData = { ...createResponse.payload?.data, _fieldMeta: {} };
            setCreatedProjectData(newProjectData);
            setSelectedView('lensFeatures');

            dispatch(GetMergedCompanyProfile({
                website: createResponse.payload?.data?.website,
                company_name: formData?.company_name,
                background: true,
            })).then(res => {
                if (res?.type !== 'GET_MERGED_COMPANY_PROFILE/fulfilled') {
                    return;
                }

                const companyDetails = res?.payload?.data?.company_info;
                const fieldMeta = res?.payload?.data?.field_meta;
                setCreatedProjectData(prev => applyCompanyEnrichment(prev, companyDetails, fieldMeta));

                const projectId = createResponse.payload?.data?.id;
                const enrichmentPatch = buildEnrichmentPatchPayload(companyDetails);
                if (projectId && Object.keys(enrichmentPatch).length > 0) {
                    dispatch(
                        PatchProjectData({
                            id: projectId,
                            data: enrichmentPatch,
                            suppressSuccessToast: true,
                        }),
                    );
                }
            });
        } catch {
            toast.error('Could not create project. Please try again.', { position: 'top-right' });
        }
    };

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    function isURL(url) {
        const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
        return urlRegex.test(url);
    }

    const disableFormBtn =
        !formData?.company_name ||
        !formData?.project_name ||
        !formData?.project_deadline ||
        !formData?.website ||
        !formData?.project_type ||
        !formData?.investment_type ||
        projectDetailButtonLoading;

    const showUrlError = formData?.website && !isURL(formData.website);

    return (
        <div className={styles.componentCard} style={{ width: '560px', maxWidth: '90vw' }}>
            <h5 className={styles.componentTitle}>Add New Project</h5>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Project Name <span className={styles.required}>*</span>
                        </p>
                        <Input className={styles.inputField} required value={formData?.project_name} onChange={handleChange} name="project_name" />
                    </div>
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Project Type <span className={styles.required}>*</span>
                        </p>
                        <Select className={styles.selectField} required value={formData?.project_type} onChange={handleChange} name="project_type">
                            <option value="">Select</option>
                            {projectTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Organisation Name <span className={styles.required}>*</span>
                        </p>
                        <Input className={styles.inputField} required value={formData?.company_name} onChange={handleChange} name="company_name" />
                    </div>
                    <div className="col-sm-12 col-md-6 mb-4">
                        <div className="d-flex align-items-center justify-content-between">
                            <p className={styles.fieldLabel}>
                                Website <span className={styles.required}>*</span>
                            </p>
                            <Tooltip withArrow content="Please enter a valid company website address in the correct format, as it is used to gather additional data.">
                                <Info12Filled role="button" color="var(--Neutral-Gray-6)" />
                            </Tooltip>
                        </div>
                        <Input
                            className={styles.inputField}
                            required
                            type="url"
                            value={formData?.website}
                            onChange={handleChange}
                            name="website"
                            placeholder="https://example.com"
                        />
                        {showUrlError && <span className={styles.errorMessage}>Please enter a URL in https:// or http:// format</span>}
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>Investor</p>
                        <Input className={styles.inputField} value={formData?.investor_name} onChange={handleChange} name="investor_name" />
                    </div>
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Investment Type <span className={styles.required}>*</span>
                        </p>
                        <Select className={styles.selectField} required value={formData?.investment_type} onChange={handleChange} name="investment_type">
                            {investmentTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>
                <div className="mb-5">
                    <p className={styles.fieldLabel}>
                        Project Deadline <span className={styles.required}>*</span>
                    </p>
                    <Input className={styles.inputField} required type="date" value={formData?.project_deadline} onChange={handleChange} name="project_deadline" />
                </div>

                <div className="d-flex align-items-center justify-content-between">
                    <button disabled={disableFormBtn || showUrlError} type="submit" className={styles.submitButton}>
                        Next
                        {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                    </button>
                    <span role="button" onClick={() => navigate('/overview')} className={styles.linkBtn}>
                        Cancel
                    </span>
                </div>
            </form>
        </div>
    );
};

export default AddProject;
